import { useState } from 'react';
import { pdf } from '@react-pdf/renderer';

import PdfReportDocument from 'Pages/TeamDashboard/CreateReview/components/PdfReport';

import { buildPdfData } from 'utlis/pdfDataBuilder';
import { generatePdfFilename } from 'utlis/pdfUtils';
import { errorReactToastify, infoReactToastify } from 'utlis/toasts';

const PDF_TOAST_STYLE = { position: 'bottom-center', style: { backgroundColor: '#EEF8FF', width: '400px' } };

const hasFeedbackForPdf = reviewData => {
  const deeperInsights = reviewData?.extra_data?.post_processes?.deeper_insights;
  const hasDeeperInsights = deeperInsights?.status === 2 && deeperInsights?.feedback?.length > 0;
  const rawFeedback = reviewData?.final_feedback || [];
  const findings = hasDeeperInsights
    ? rawFeedback.filter(item => item.category !== 'deeper_insight' && !item.isDeeperInsight)
    : rawFeedback;

  return findings.length > 0 || hasDeeperInsights;
};

const useDownloadPdfReport = reviewData => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateAndDownload = async () => {
    if (isGenerating || !reviewData) {
      return;
    }

    if (!hasFeedbackForPdf(reviewData)) {
      infoReactToastify(
        'No feedback is available to generate the PDF.',
        PDF_TOAST_STYLE
      );

      return;
    }

    setIsGenerating(true);
    infoReactToastify(
      'Generating your PDF report...',
      PDF_TOAST_STYLE
    );

    try {
      // Build the PDF data (fetches images, normalizes data)
      const pdfData = await buildPdfData(reviewData);

      // Generate PDF blob
      const blob = await pdf(<PdfReportDocument data={pdfData} />).toBlob();

      // Download the blob
      const filename = generatePdfFilename(
        reviewData.extra_data?.file_name || reviewData.extra_data?.review_frame_name
      );

      // eslint-disable-next-line compat/compat
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // eslint-disable-next-line compat/compat
      URL.revokeObjectURL(blobUrl);
    }
    catch (error) {
      console.error('PDF generation failed:', error);
      errorReactToastify(
        'Failed to generate PDF. Please try again.',
        { position: 'bottom-center', style: { backgroundColor: '#FDF0F0', width: '400px' } }
      );
    }
    finally {
      setIsGenerating(false);
    }
  };

  return { isGenerating, generateAndDownload };
};

export default useDownloadPdfReport;
